import { useEffect, useRef, useState } from "react";
import { FaLanguage, FaTimes, FaCheck } from "react-icons/fa";
import {
  waitForTranslateWidget,
  getStoredLanguage,
  setLanguage,
  flagEmoji,
} from "./googleTranslate";
import useLanguages from "../hooks/useLanguages";
import { findDefaultLanguage } from "../services/languageService";
import "./GoogleTranslateFloat.css";

/**
 * Floating translate button — left side, stacked above CallFloat,
 * matching the same shape/shadow/tooltip pattern as the other
 * floats in src/Whatsapp/*.jsx. Clicking it reveals a small panel
 * containing Google's own default "Website Translator" widget.
 *
 * The #google_translate_element div stays permanently mounted in
 * the DOM (never conditionally rendered) — Google's script binds to
 * this exact node once, at init, and doesn't reattach if it's
 * removed and re-created later. Only the wrapping panel's *visual*
 * visibility toggles, via a CSS class, so the widget survives the
 * panel being opened and closed. See src/Language/googleTranslate.js.
 */
export default function GoogleTranslateFloat() {
  const [open, setOpen] = useState(false);
  // null = still checking / not opened yet, true = widget confirmed up,
  // false = Google's script never produced a <select> (blocked domain,
  // offline, etc) — shown as a "translation unavailable" message
  // instead of a silently empty panel.
  const [widgetReady, setWidgetReady] = useState(null);
  const rootRef = useRef(null);
  const toggleBtnRef = useRef(null);
  const wasOpenRef = useRef(false);

  const { languages } = useLanguages();
  const defaultLang = findDefaultLanguage(languages);
  const [currentCode, setCurrentCode] = useState(null);
  const [switching, setSwitching] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!defaultLang) return;
    setCurrentCode((prev) => prev || getStoredLanguage(defaultLang.code));
  }, [defaultLang]);

  const closePanel = () => setOpen(false);

  const pickLanguage = (code) => {
    if (!defaultLang || code === currentCode) return;
    setError(null);
    setSwitching(code);
    setLanguage(code, defaultLang.code).then((res) => {
      setSwitching(null);
      if (res.ok) {
        setCurrentCode(code);
        setError(null);
        setOpen(false);
        return;
      }
      if (res.reason === "unavailable") {
        setWidgetReady(false);
      } else if (res.reason === "unsupported") {
        setError("That language isn't supported by Google Translate. Please pick another one.");
      } else {
        setError("We couldn't switch to that language just now. Please try again or pick another one.");
      }
    });
  };



  useEffect(() => {
    if (!open) return undefined;

    let cancelled = false;
    waitForTranslateWidget().then((ready) => {
      if (!cancelled) setWidgetReady(ready);
    });

    const handleClickOutside = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) closePanel();
    };
    const handleEscape = (e) => { if (e.key === "Escape") closePanel(); };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      cancelled = true;
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open]);

  // Return focus to the toggle button whenever the panel closes, if
  // focus was left somewhere inside the panel (e.g. on Google's own
  // <select> or the close button). Without this, aria-hidden gets set
  // on an ancestor of the still-focused element the instant the panel
  // closes via outside-click/Escape — an ARIA violation that can strand
  // screen-reader focus in a hidden subtree.
  useEffect(() => {
    if (wasOpenRef.current && !open) {
      if (rootRef.current && rootRef.current.contains(document.activeElement)) {
        toggleBtnRef.current?.focus();
      }
    }
    wasOpenRef.current = open;
  }, [open]);

  return (
    <div className="gt-float notranslate" translate="no" ref={rootRef}>
      <button
        type="button"
        ref={toggleBtnRef}
        className="gt-toggle-btn"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label="Translate this page"
      >
        <span className="gt-toggle-tooltip">Translate</span>
        <FaLanguage />
      </button>

      <div
        className={`gt-panel ${open ? "open" : ""}`}
        role="dialog"
        aria-label="Translate this page"
        aria-hidden={!open}
      >
        <div className="gt-panel-head">
          <h3>Translate</h3>
          <button
            type="button"
            className="gt-close"
            onClick={closePanel}
            aria-label="Close translate menu"
          >
            <FaTimes />
          </button>
        </div>
        <div className="gt-panel-body">
          {widgetReady === false && (
            <p className="gt-panel-error">
              Translation is unavailable right now — it may be blocked by
              your browser or a privacy extension, or you may be offline.
            </p>
          )}

          {widgetReady !== false && error && (
            <p className="gt-panel-error gt-panel-error-strong" role="alert">{error}</p>
          )}


          {widgetReady !== false && languages.length > 0 && (
            <ul className="gt-lang-list" role="listbox" aria-label="Choose a language">
              {languages.map((lang) => {
                const active = lang.code === currentCode;
                return (
                  <li key={lang.code}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={active}
                      className={`gt-lang-item ${active ? "active" : ""}`}
                      onClick={() => pickLanguage(lang.code)}
                      disabled={switching !== null}
                    >
                      <img
                        className="gt-lang-flag"
                        src={`https://flagcdn.com/w40/${(lang.flag_code || "").toLowerCase()}.png`}
                        alt=""
                        aria-hidden="true"
                        loading="lazy"
                        width="26"
                        height="20"
                        onError={(e) => {
                          e.currentTarget.replaceWith(
                            Object.assign(document.createElement("span"), {
                              className: "gt-lang-flag gt-lang-flag-fallback",
                              textContent: flagEmoji(lang.flag_code),
                            })
                          );
                        }}
                      />

                      <span className="gt-lang-text">
                        <span className="gt-lang-name">{lang.name}</span>
                        {lang.native_name && lang.native_name !== lang.name && (
                          <span className="gt-lang-native">{lang.native_name}</span>
                        )}
                      </span>
                      {active && <FaCheck className="gt-lang-check" aria-hidden="true" />}
                      {switching === lang.code && (
                        <span className="gt-lang-spinner" aria-hidden="true" />
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          {/* Google's own widget — permanently mounted (its script binds to
              this exact node once) but visually hidden; the flag list above
              drives its <select>. */}
          <div
            id="google_translate_element"
            className="google-translate-widget notranslate gt-widget-hidden"
            translate="no"
          />
        </div>

      </div>
    </div>
  );
}
